import { Pressable } from 'components/Pressable';
import { TEXT_SIZE_NORMAL, Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import { useCallbackState } from 'lib/useCallbackState';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextInputProps,
  ViewStyle,
} from 'react-native';
import {
  CodeField as ExternalCodeField,
  Cursor,
  useBlurOnFulfill,
} from 'react-native-confirmation-code-field';

const CELL_COUNT = 6;

type Props = {
  rootStyle?: StyleProp<ViewStyle>;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  autoFocusEveryRender?: boolean;
  externalValue?: string;
  isOTP?: boolean;
};

export const CodeField: React.FC<Props> = ({
  autoFocus,
  autoFocusEveryRender,
  externalValue,
  isOTP,
  onChange,
  rootStyle,
}) => {
  const [value, setValue] = useCallbackState<string>('', onChange, 0);
  const ref = useBlurOnFulfill({ cellCount: CELL_COUNT, value });
  const hasFocussedRef = useRef<boolean>(false);

  useEffect(() => {
    if (autoFocusEveryRender) {
      hasFocussedRef.current = true;
      ref.current?.focus();
    } else if (!hasFocussedRef.current && autoFocus) {
      hasFocussedRef.current = true;
      ref.current?.focus();
    }
  });

  useEffect(() => {
    setValue(externalValue || '');
  }, [externalValue, setValue]);

  let textContentType: TextInputProps['textContentType'] = 'none';
  let autoComplete: TextInputProps['autoComplete'] = 'off';

  if (isOTP) {
    textContentType = 'oneTimeCode';
    if (Platform.OS === 'web') {
      autoComplete = 'one-time-code';
    }
  }

  return (
    <ExternalCodeField
      ref={ref}
      caretHidden={false}
      cellCount={CELL_COUNT}
      value={value}
      rootStyle={[styles.rootStyle, rootStyle]}
      textInputStyle={{
        textAlign: 'center',
      }}
      onChangeText={setValue}
      keyboardType="number-pad"
      textContentType={textContentType}
      autoComplete={autoComplete}
      renderCell={({ index, isFocused, symbol }) => (
        <Pressable
          key={index}
          onPress={() => {
            if (!ref.current?.isFocused()) {
              ref.current?.focus();
              setValue(value.slice(0, index));
            }
          }}
          style={[
            styles.cell,
            isFocused && styles.focusCell,
            !index && styles.cellFirst,
            { zIndex: 100 },
          ]}
        >
          <Txt style={styles.text}>
            {symbol || (isFocused ? <Cursor /> : null)}
          </Txt>
        </Pressable>
      )}
    />
  );
};

const styles = StyleSheet.create({
  cell: {
    alignContent: 'center',
    borderColor: '#f1f1f1',
    borderLeftWidth: 1,
    flex: 1,
    height: 48,
    justifyContent: 'center',
  },
  cellFirst: {
    borderLeftWidth: 0,
  },
  focusCell: {
    outlineColor: theme.colors.blue,
    outlineWidth: 1,
    outlineStyle: 'solid',
    outlineOffset: Platform.OS === 'web' ? 0 : -2,
    borderRadius: Platform.OS === 'web' ? 8 : 6,
  },
  rootStyle: {
    backgroundColor: 'white',
    overflow: 'hidden',
  },
  text: {
    color: '#696969',
    fontSize: TEXT_SIZE_NORMAL,
    textAlign: 'center',
  },
});
